import type { Locale } from "@zoya/i18n";
import type {
  CatalogResponse,
  CatalogSort,
  ProductJourney,
  PublicCatalogProduct,
} from "./types.js";

const responseJson = async <Result>(response: Response): Promise<Result> => {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string };
    } | null;
    // The edge proxy answers an oversized body with a bare 413 (no JSON).
    throw new Error(
      body?.error?.code ??
        (response.status === 404
          ? "NOT_FOUND"
          : response.status === 413
            ? "BODY_TOO_LARGE"
            : "SERVICE_UNAVAILABLE"),
    );
  }
  return (await response.json()) as Result;
};

let csrfToken = sessionStorage.getItem("zoya.storefront.csrf") ?? "";
let customerCsrfToken =
  sessionStorage.getItem("zoya.storefront.customer.csrf") ?? "";
const resetGuestSession = () => {
  csrfToken = "";
  sessionStorage.removeItem("zoya.storefront.csrf");
};
const mutationHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  "X-CSRF-Token": csrfToken,
});

export type CartCoupon = {
  code: string;
  valid: boolean;
  discountAmountMinor: string;
  reason?: string;
};

export type Cart = {
  publicId: string;
  revision: number;
  currency: "EGP";
  lineCount: number;
  subtotalAmountMinor: string;
  totalAmountMinor?: string;
  coupon?: CartCoupon | null;
  items: Array<{
    publicId: string;
    slug: string;
    title: string;
    image: string | null;
    configuration: Record<string, string | number | boolean>;
    customerDesign: CustomerDesignSubmission | null;
    quantity: number;
    unitAmountMinor: string;
    lineAmountMinor: string;
    priceState: "CURRENT" | "CHANGED" | "UNAVAILABLE";
    candidateUnitAmountMinor?: string;
    candidateLineAmountMinor?: string;
  }>;
};

export type CustomerDesignFile = {
  attachmentPublicId: string;
  descriptorVersion: 1;
  displayName: string;
  mediaType: string;
  byteSize: string;
  sha256: string;
  uploadedAt: string;
  state: "READY_FOR_REVIEW";
};

export type CustomerDesignSubmission = Omit<CustomerDesignFile, "state"> & {
  notes: string | null;
};

export type CustomerDesignRequest = {
  requestPublicId: string;
  title: string;
  notes?: string | null;
  status: string;
  quantity?: number;
  targetDate?: string | null;
  attachmentSnapshot?: Omit<CustomerDesignFile, "state">;
  submittedAt: string;
  duplicate?: boolean;
};

export const ensureGuest = async () => {
  if (csrfToken) return;
  const result = await responseJson<{ csrfToken: string }>(
    await fetch("/api/v1/guest/session", { method: "POST" }),
  );
  csrfToken = result.csrfToken;
  sessionStorage.setItem("zoya.storefront.csrf", csrfToken);
};

export const getCart = async () => {
  const read = async () => responseJson<Cart>(await fetch("/api/v1/cart"));
  await ensureGuest();
  try {
    return await read();
  } catch (error) {
    if (!(error instanceof Error) || error.message !== "GUEST_SESSION_INVALID")
      throw error;
    resetGuestSession();
    await ensureGuest();
    return read();
  }
};

export const addCartItem = async (input: {
  slug: string;
  quantity: number;
  configuration: Record<string, string | number | boolean>;
}) => {
  const add = async () =>
    responseJson<Cart>(
      await fetch("/api/v1/cart/items", {
        method: "POST",
        headers: mutationHeaders(),
        body: JSON.stringify(input),
      }),
    );
  await ensureGuest();
  try {
    return await add();
  } catch (error) {
    if (!(error instanceof Error) || error.message !== "GUEST_SESSION_INVALID")
      throw error;
    resetGuestSession();
    await ensureGuest();
    return add();
  }
};

export const addAuthenticatedCartItem = async (input: {
  slug: string;
  quantity: number;
  configuration: Record<string, string | number | boolean>;
  customerDesign?: { attachmentPublicId: string; notes?: string };
}) => {
  const add = async () =>
    responseJson<Cart>(
      await fetch("/api/v1/cart/items", {
        method: "POST",
        headers: {
          ...mutationHeaders(),
          "X-Customer-CSRF-Token": customerCsrfToken,
        },
        body: JSON.stringify(input),
      }),
    );
  await ensureGuest();
  try {
    return await add();
  } catch (error) {
    if (!(error instanceof Error) || error.message !== "GUEST_SESSION_INVALID")
      throw error;
    resetGuestSession();
    await ensureGuest();
    return add();
  }
};

export const uploadCustomerDesign = async (file: File) =>
  responseJson<CustomerDesignFile>(
    await fetch("/api/v1/customer-design-files", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": file.type,
        "X-CSRF-Token": customerCsrfToken,
        "X-Zoya-File-Name": encodeURIComponent(file.name),
      },
      body: file,
    }),
  );

export const removeCustomerDesign = async (attachmentPublicId: string) =>
  responseJson<{ removed: true }>(
    await fetch(
      `/api/v1/customer-design-files/${encodeURIComponent(attachmentPublicId)}`,
      {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": customerCsrfToken,
        },
      },
    ),
  );

export const submitCustomerDesignRequest = (
  input: {
    attachmentPublicId: string;
    title: string;
    notes?: string;
    quantity: number;
    targetDate?: string;
  },
  idempotencyKey: string,
) =>
  fetch("/api/v1/customer-design-requests", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      ...customerMutationHeaders(),
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(input),
  }).then(responseJson<CustomerDesignRequest>);

export const getCustomerDesignRequests = () =>
  fetch("/api/v1/customer/customer-design-requests?limit=25").then(
    responseJson<CustomerDesignRequest[]>,
  );

export const updateCartItem = async (publicId: string, quantity: number) =>
  responseJson<Cart>(
    await fetch(`/api/v1/cart/items/${encodeURIComponent(publicId)}`, {
      method: "PATCH",
      headers: mutationHeaders(),
      body: JSON.stringify({ quantity }),
    }),
  );

export const removeCartItem = async (publicId: string) =>
  responseJson<Cart>(
    await fetch(`/api/v1/cart/items/${encodeURIComponent(publicId)}`, {
      method: "DELETE",
      headers: mutationHeaders(),
    }),
  );

export const repriceCart = async () =>
  responseJson<Cart>(
    await fetch("/api/v1/cart/reprice", {
      method: "POST",
      headers: mutationHeaders(),
    }),
  );

export const acceptCartPrices = async () =>
  responseJson<Cart>(
    await fetch("/api/v1/cart/accept-prices", {
      method: "POST",
      headers: mutationHeaders(),
    }),
  );

export const applyCartCoupon = async (code: string) =>
  responseJson<Cart>(
    await fetch("/api/v1/cart/coupon", {
      method: "POST",
      headers: mutationHeaders(),
      body: JSON.stringify({ code }),
    }),
  );

export const removeCartCoupon = async () =>
  responseJson<Cart>(
    await fetch("/api/v1/cart/coupon", {
      method: "DELETE",
      headers: mutationHeaders(),
    }),
  );

export const verifyGuestContact = async (email: string, code: string) =>
  responseJson<{ verified: true; email: string }>(
    await fetch("/api/v1/guest/contact/verify", {
      method: "POST",
      headers: mutationHeaders(),
      body: JSON.stringify({ email, code }),
    }),
  );

export type OrderConfirmation = {
  orderPublicId: string;
  orderNumber: string;
  commercialStatus: "CONFIRMED";
  totalAmountMinor: string;
  currency: "EGP";
  payment: { method: string; status: string };
  fulfillment: { mode: string; status: string };
};

export const checkout = async (
  input: Record<string, unknown>,
  idempotencyKey: string,
) =>
  responseJson<OrderConfirmation>(
    await fetch("/api/v1/checkout", {
      method: "POST",
      headers: {
        ...mutationHeaders(),
        "Idempotency-Key": idempotencyKey,
        ...(customerCsrfToken
          ? { "X-Customer-CSRF-Token": customerCsrfToken }
          : {}),
      },
      body: JSON.stringify(input),
    }),
  );

export type Customer = {
  customerPublicId: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  phone: string | null;
  locale: Locale;
};

export const registerCustomer = (input: Record<string, unknown>) =>
  fetch("/api/v1/auth/register", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  }).then(responseJson<{ accepted: true }>);

export const loginCustomer = async (email: string, password: string) => {
  const result = await responseJson<{
    csrfToken: string;
    customerPublicId: string;
  }>(
    await fetch("/api/v1/auth/login", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    }),
  );
  customerCsrfToken = result.csrfToken;
  sessionStorage.setItem("zoya.storefront.customer.csrf", customerCsrfToken);
  return result;
};

export const refreshCustomerCsrf = async () => {
  const result = await responseJson<{ csrfToken: string }>(
    await fetch("/api/v1/auth/csrf"),
  );
  customerCsrfToken = result.csrfToken;
  sessionStorage.setItem("zoya.storefront.customer.csrf", customerCsrfToken);
  return result.csrfToken;
};

const customerMutationHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  "X-CSRF-Token": customerCsrfToken,
});
export const getCustomerQuotations = () =>
  fetch("/api/v1/customer/quotations").then(responseJson<unknown[]>);
export const getCustomerQuotation = (id: string) =>
  fetch(`/api/v1/customer/quotations/${encodeURIComponent(id)}`).then(
    responseJson<Record<string, unknown>>,
  );
export const acceptCustomerQuotation = async (
  id: string,
  body: unknown,
  idempotencyKey: string,
) => {
  await refreshCustomerCsrf();
  return fetch(`/api/v1/customer/quotations/${encodeURIComponent(id)}/accept`, {
    method: "POST",
    headers: {
      ...customerMutationHeaders(),
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(body),
  }).then(responseJson<{ status: string; orderPublicId: string | null }>);
};

export const getCustomer = () =>
  fetch("/api/v1/customer/me").then(responseJson<Customer>);

export const claimCustomerCart = async () => {
  const claim = async () =>
    responseJson<Cart>(
      await fetch("/api/v1/cart/claim", {
        method: "POST",
        headers: customerMutationHeaders(),
      }),
    );
  await ensureGuest();
  try {
    return await claim();
  } catch (error) {
    if (!(error instanceof Error) || error.message !== "GUEST_SESSION_INVALID")
      throw error;
    resetGuestSession();
    await ensureGuest();
    return claim();
  }
};

export const prepareAuthenticatedCheckoutCart = async () => {
  const customer = await getCustomer();
  if (!customer.emailVerified) throw new Error("CONTACT_NOT_VERIFIED");
  await refreshCustomerCsrf();
  const cart = await claimCustomerCart();
  return { customer, cart };
};

export const logoutCustomer = async () => {
  await responseJson<{ loggedOut: true }>(
    await fetch("/api/v1/auth/logout", {
      method: "POST",
      headers: customerMutationHeaders(),
    }),
  );
  customerCsrfToken = "";
  sessionStorage.removeItem("zoya.storefront.customer.csrf");
};

export const requestPasswordReset = (email: string) =>
  fetch("/api/v1/auth/forgot-password", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  }).then(responseJson<{ accepted: true }>);

export const resetCustomerPassword = (token: string, password: string) =>
  fetch("/api/v1/auth/reset-password", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  }).then(responseJson<{ reset: true }>);

export const verifyCustomerEmail = (token: string) =>
  fetch("/api/v1/auth/verify-email", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  }).then(responseJson<{ verified: true }>);

export const getCustomerOrders = () =>
  fetch("/api/v1/customer/orders?limit=25").then(
    responseJson<{
      data: Array<Record<string, unknown>>;
      nextCursor: string | null;
    }>,
  );

export const getCustomerOrder = (publicId: string) =>
  fetch(`/api/v1/customer/orders/${encodeURIComponent(publicId)}`, {
    cache: "no-store",
  }).then(responseJson<Record<string, unknown>>);

export const getCustomerTracking = (publicId: string) =>
  fetch(`/api/v1/customer/orders/${encodeURIComponent(publicId)}/tracking`, {
    cache: "no-store",
  }).then(responseJson<Record<string, unknown>>);

export const getCustomerAddresses = () =>
  fetch("/api/v1/customer/addresses").then(
    responseJson<Array<Record<string, unknown>>>,
  );

export const createCustomerAddress = (input: Record<string, unknown>) =>
  fetch("/api/v1/customer/addresses", {
    method: "POST",
    headers: customerMutationHeaders(),
    body: JSON.stringify(input),
  }).then(responseJson<{ addressPublicId: string }>);

export const updateCustomerProfile = (input: Record<string, unknown>) =>
  fetch("/api/v1/customer/profile", {
    method: "PATCH",
    headers: customerMutationHeaders(),
    body: JSON.stringify(input),
  }).then(responseJson<{ updated: true }>);

export const changeCustomerPassword = (
  currentPassword: string,
  newPassword: string,
) =>
  fetch("/api/v1/customer/change-password", {
    method: "POST",
    headers: customerMutationHeaders(),
    body: JSON.stringify({ currentPassword, newPassword }),
  }).then(responseJson<{ changed: true }>);

export const getCustomerNotifications = () =>
  fetch("/api/v1/customer/notifications").then(
    responseJson<Array<Record<string, unknown>>>,
  );

export type IdeaRequest = {
  requestPublicId: string;
  title: string;
  status: string;
  quantity?: number;
  targetDate?: string | null;
  budgetAmountMinor?: string | null;
  submittedAt: string;
  duplicate?: boolean;
};

export const submitIdeaRequest = (
  input: {
    slug: string;
    title: string;
    description: string;
    quantity: number;
    targetDate?: string;
    budgetAmountMinor?: string;
  },
  idempotencyKey: string,
) =>
  fetch("/api/v1/idea-requests", {
    method: "POST",
    headers: {
      ...customerMutationHeaders(),
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(input),
  }).then(responseJson<IdeaRequest>);

export const getCustomerIdeaRequests = () =>
  fetch("/api/v1/customer/idea-requests?limit=25").then(
    responseJson<IdeaRequest[]>,
  );

export const getCatalog = async (input: {
  locale: Locale;
  category?: string;
  journey?: ProductJourney;
  minPriceMinor?: string;
  maxPriceMinor?: string;
  search?: string;
  sort?: CatalogSort;
  signal?: AbortSignal;
}) => {
  const query = new URLSearchParams({ locale: input.locale, limit: "12" });
  if (input.category) query.set("category", input.category);
  if (input.journey) query.set("journey", input.journey);
  if (input.minPriceMinor) query.set("minPrice", input.minPriceMinor);
  if (input.maxPriceMinor) query.set("maxPrice", input.maxPriceMinor);
  if (input.search) query.set("search", input.search);
  if (input.sort) query.set("sort", input.sort);
  return responseJson<CatalogResponse>(
    await fetch(`/api/v1/catalog?${query}`, {
      headers: { Accept: "application/json" },
      ...(input.signal ? { signal: input.signal } : {}),
    }),
  );
};

export type ShopperAudienceIcon =
  | "student"
  | "doctor"
  | "engineer"
  | "teacher"
  | "parent"
  | "artist"
  | "professional"
  | "developer"
  | "gift"
  | "home"
  | "couple"
  | "general";

export type ShopperAudience = {
  publicId: string;
  slug: string;
  icon: ShopperAudienceIcon;
  name: string;
  tagline: string | null;
};

export const getAudiences = async (locale: Locale, signal?: AbortSignal) =>
  responseJson<{ data: ShopperAudience[] }>(
    await fetch(`/api/v1/audiences?locale=${locale}`, {
      headers: { Accept: "application/json" },
      ...(signal ? { signal } : {}),
    }),
  );

export const getAudienceProducts = async (
  slug: string,
  locale: Locale,
  signal?: AbortSignal,
) =>
  responseJson<{ audience: ShopperAudience; data: PublicCatalogProduct[] }>(
    await fetch(
      `/api/v1/audiences/${encodeURIComponent(slug)}/products?locale=${locale}&limit=6`,
      {
        headers: { Accept: "application/json" },
        ...(signal ? { signal } : {}),
      },
    ),
  );

export const getProduct = async (
  slug: string,
  locale: Locale,
  signal?: AbortSignal,
) =>
  responseJson<PublicCatalogProduct>(
    await fetch(
      `/api/v1/catalog/${encodeURIComponent(slug)}?locale=${locale}`,
      {
        headers: { Accept: "application/json" },
        ...(signal ? { signal } : {}),
      },
    ),
  );

export type PricingResponse =
  | {
      outcome: "PRICED";
      currency: "EGP";
      unitAmountMinor: string;
      quantity: number;
      totalAmountMinor: string;
      breakdown: Array<{
        code: string;
        label: { en: string; ar: string };
        amountMinor: string;
      }>;
    }
  | { outcome: "QUOTE_REQUIRED"; currency: "EGP"; reasonCode: string };

export const evaluatePrice = async (input: {
  slug: string;
  quantity: number;
  configuration: Record<string, string | number | boolean>;
  signal: AbortSignal;
}) =>
  responseJson<PricingResponse>(
    await fetch("/api/v1/pricing/evaluations", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        slug: input.slug,
        quantity: input.quantity,
        configuration: input.configuration,
      }),
      signal: input.signal,
    }),
  );

export const getQuotation = async (
  publicId: string,
  accessSecret: string,
  signal: AbortSignal,
) =>
  responseJson<Record<string, unknown>>(
    await fetch(`/api/v1/quotations/${encodeURIComponent(publicId)}`, {
      headers: {
        Accept: "application/json",
        "X-Quotation-Access": accessSecret,
      },
      signal,
    }),
  );

export type RatingSummary = {
  count: number;
  average: number;
  distribution: Record<"1" | "2" | "3" | "4" | "5", number>;
};
export type ProductReview = {
  publicId: string;
  rating: number;
  title: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
};
export type ProductReviews = {
  summary: RatingSummary;
  reviews: ProductReview[];
};
export type MyReview = {
  authenticated: true;
  eligible: boolean;
  review: (ProductReview & { state: "PUBLISHED" | "HIDDEN" }) | null;
};
export type WishlistItem = {
  productPublicId: string;
  slug: string;
  addedAt: string;
};

export const getProductReviews = (slug: string) =>
  fetch(`/api/v1/reviews/${encodeURIComponent(slug)}`, {
    headers: { Accept: "application/json" },
  }).then(responseJson<ProductReviews>);
export const getMyReview = (slug: string) =>
  fetch(`/api/v1/customer/reviews/${encodeURIComponent(slug)}`, {
    headers: { Accept: "application/json" },
  }).then(responseJson<MyReview | { authenticated: false }>);
export const saveMyReview = async (
  slug: string,
  input: { rating: number; title: string; body: string },
) => {
  await refreshCustomerCsrf();
  return fetch(`/api/v1/customer/reviews/${encodeURIComponent(slug)}`, {
    method: "PUT",
    headers: customerMutationHeaders(),
    body: JSON.stringify(input),
  }).then(responseJson<ProductReview>);
};
export const deleteMyReview = async (slug: string) => {
  await refreshCustomerCsrf();
  return fetch(`/api/v1/customer/reviews/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    headers: customerMutationHeaders(),
  }).then(responseJson<{ removed: true }>);
};

export const getWishlist = () =>
  fetch("/api/v1/customer/wishlist", {
    headers: { Accept: "application/json" },
  }).then(
    responseJson<
      { authenticated: true; items: WishlistItem[] } | { authenticated: false }
    >,
  );
export const addToWishlist = async (slug: string) => {
  await refreshCustomerCsrf();
  return fetch(`/api/v1/customer/wishlist/${encodeURIComponent(slug)}`, {
    method: "PUT",
    headers: customerMutationHeaders(),
  }).then(responseJson<{ slug: string }>);
};
export const removeFromWishlist = async (slug: string) => {
  await refreshCustomerCsrf();
  return fetch(`/api/v1/customer/wishlist/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    headers: customerMutationHeaders(),
  }).then(responseJson<{ removed: true }>);
};

export type ReturnReason =
  | "DEFECTIVE"
  | "DAMAGED_IN_TRANSIT"
  | "NOT_AS_DESCRIBED"
  | "WRONG_ITEM"
  | "CHANGED_MIND"
  | "OTHER";
export type CustomerReturn = {
  returnPublicId: string;
  orderPublicId: string;
  orderNumber: string;
  reason: ReturnReason;
  details: string | null;
  preferredResolution: "REFUND" | "REPLACEMENT";
  status: "REQUESTED" | "APPROVED" | "REJECTED" | "RECEIVED" | "RESOLVED";
  resolution: "REFUND_OWED" | "REPLACEMENT_ISSUED" | "NO_ACTION" | null;
  staffNote: string | null;
  requestedAt: string;
  decidedAt: string | null;
  items: Array<{ orderItemPublicId: string; quantity: number }>;
};
export type EligibleReturnOrder = {
  orderPublicId: string;
  orderNumber: string;
  windowDays: number;
  deadline: string;
  items: Array<{
    orderItemPublicId: string;
    title: { en: string; ar: string };
    orderedQuantity: number;
    returnableQuantity: number;
  }>;
};

export const getCustomerReturns = () =>
  fetch("/api/v1/customer/returns", {
    headers: { Accept: "application/json" },
  }).then(responseJson<CustomerReturn[]>);
export const getEligibleReturnOrders = () =>
  fetch("/api/v1/customer/returns/eligible-orders", {
    headers: { Accept: "application/json" },
  }).then(responseJson<EligibleReturnOrder[]>);
export const createCustomerReturn = async (
  input: {
    orderPublicId: string;
    items: Array<{ orderItemPublicId: string; quantity: number }>;
    reason: ReturnReason;
    details: string;
    preferredResolution: "REFUND" | "REPLACEMENT";
  },
  idempotencyKey: string,
) => {
  await refreshCustomerCsrf();
  return fetch("/api/v1/customer/returns", {
    method: "POST",
    headers: {
      ...customerMutationHeaders(),
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(input),
  }).then(responseJson<CustomerReturn>);
};

export type PaymentInstructions = {
  bankTransfer: {
    bankName: string;
    accountName: string;
    accountNumber: string;
  } | null;
  instapay: { address: string } | null;
};

export const getPaymentInstructions = async () =>
  responseJson<PaymentInstructions>(
    await fetch("/api/v1/payment-instructions", {
      headers: { Accept: "application/json" },
    }),
  );
